import { useCallback, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Avatar,
  ButtonBase,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Grid2 as Grid,
  useTheme,
  FormHelperText,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import { StorageReference } from 'firebase/storage';
import LinearProgress from '@mui/material/LinearProgress';
import pluralize from 'pluralize';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { useResponsive } from '@/hooks';
import { useAccessControl } from '@/features/access-control';
import useSnackbar from '@/hooks/useSnackbar';
import { ONE_MB, FOUR_MB, BASE_IMAGE_URL } from '@/constants/global';
import Thumbnail from './components/Thumbnail';
import { MediaResponse, useDeleteImageMutation, useLazyFetchImagesQuery, useUploadImageMutation } from '@/api/mediaApi';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, Cancel01Icon, Delete02Icon, Image02Icon, ImageUploadIcon, Tree01Icon, Upload01Icon } from "@hugeicons/core-free-icons";
import LoadingButton from '@mui/lab/LoadingButton';
import { getThumbnailImage } from '@/utils/media';
import './MediaManager.scss';

export interface Metadata {
  name: string;
  size: number;
  timeCreated: string;
}

const MAX_FILE_SIZE = FOUR_MB;

export interface Media extends Metadata {
  src: string;
  fullPath: string;
  ref?: StorageReference;
  updated: string;
  contentType: string;
}

const MAX_FILES = 100;

const INITIAL_MEDIA_RESULTS = {
  images: [],
  nextPageToken: undefined,
}

interface Props {
  label?: string;
  value?: string;
  multiple?: boolean;
  helperText?: string;
  onChange: (selection: string[]) => void;
  onDelete?: () => void;
}

const MediaManager = ({
  label,
  value,
  multiple = false,
  helperText,
  onChange,
  onDelete,
}: Props) => {
  const { palette } = useTheme();
  const { hasAccess } = useAccessControl();
  const { isMobile } = useResponsive();
  const scrollContainer = useRef<HTMLDivElement>(null);
  const [openSnackbar] = useSnackbar();

  const { organizationId, templateId } = useParams<{
    organizationId?: string;
    templateId?: string;
  }>();

  const canUpload = !!templateId || !!hasAccess('upload.media');
  const canAdd = !!templateId || !!hasAccess('add.media');
  const canDelete = !!templateId || !!hasAccess('delete.media');

  const [isOpen, setIsOpen] = useState(false);

  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [mediaResults, setMediaResults] = useState<{
    images: string[];
    nextPageToken?: string | null;
  }>(INITIAL_MEDIA_RESULTS)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const [fetchImages, imagesQuery] = useLazyFetchImagesQuery();
  const [uploadImage, uploadImageMutation] = useUploadImageMutation();
  const [deleteImage, deleteImageMutation] = useDeleteImageMutation();

  const handleFetchImages = async ({ reset } = { reset: false }) => {
    if (!templateId && !organizationId) {
      return;
    }

    const { isError, data } = await fetchImages({ organizationId, templateId, nextPageToken: reset ? undefined : mediaResults?.nextPageToken as string | undefined });
      
    if (isError) {
      openSnackbar('Error cargando las imágenes');
    } else {
      setMediaResults({
        images: reset ? data?.images : [...mediaResults.images, ...(data?.images as string[])],
        nextPageToken: data?.nextPageToken
      } as MediaResponse);
    }
  }

  const [infiniteRef, ] = useInfiniteScroll({
    loading: imagesQuery.isLoading,
    hasNextPage: isOpen && mediaResults?.nextPageToken !== null,
    onLoadMore: handleFetchImages,
    rootMargin: '0px 0px 800px 0px',
    delayInMs: 500,
  });

  const newInfiniteRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      infiniteRef(node);
    }
  }, [infiniteRef]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file || (!organizationId && !templateId)) return;

      if (file.size >= MAX_FILE_SIZE) {
        openSnackbar(
          `El archivo supera el límite permitido de ${
            MAX_FILE_SIZE / ONE_MB
          }MB`,
          'error'
        );
        return;
      }

      const result = await uploadImage({ file, organizationId, templateId });

      if ('error' in result) {
        const error = result.error as any;
        if (error.status === 409) {
          openSnackbar('El archivo ya existe, seleccionalo de la galería', 'error');
        } else {
          openSnackbar('Ocurrió un error al cargar la imagen', 'error');
        }
      } else if ('data' in result) {
        const data = result.data;
        openSnackbar('El archivo se cargó existosamente', 'success');
        if (isOpen) handleClose();
        if (templateId) {
          onChange([data.url]);
        } else {
          onChange([data.fileName]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, templateId, isOpen]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop,
  });

  const handleClose = () => {
    handleClearSelection();
    setMediaResults(INITIAL_MEDIA_RESULTS);
    setIsOpen(false);
  };

  const handleSelection = (image: string) => {
    if (multiple) {
      const newSelection = new Set(selectedImages);

      if (newSelection.has(image)) {
        newSelection.delete(image);
      } else {
        newSelection.add(image);
      }

      setSelectedImages([...newSelection]);
      return;
    }

    setSelectedImages([image]);
  };

  const handleClearSelection = () => {
    setSelectedImages([]);
  };

  const handleDelete = useCallback(async () => {
    if ((!organizationId && !templateId) || !selectedImages?.length) return;

    const result = await deleteImage({
      fileNames: selectedImages,
      templateId,
      organizationId,
    });

    if ('error' in result) {
      const error = result.error as any;
      if (error?.statusCode === 404) {
        openSnackbar('Imagen no encontrada', 'error');
      }
    } else if ('data' in result) {
      scrollContainer?.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      handleFetchImages({
        reset: true
      });
      openSnackbar('Imagen eliminada', 'success');
    }

    handleClearSelection();
  }, [organizationId, selectedImages]);

  const handleChange = (type: 'original' | '800x500' | '150x150' = 'original') => {
    if (templateId) {
      const imageUrls = selectedImages.map((image) => {
        if (type === 'original') {
          const bucket = BASE_IMAGE_URL.split('/').pop();
          const path = encodeURIComponent(`images/templates/${templateId}/${image}`);
          return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${path}?alt=media`;
        }
        return getThumbnailImage({ templateId, file: image, size: type });
      });
      onChange(imageUrls as string[]);
    } else {
      onChange(selectedImages);
    }
    handleMenuClose();
    handleClose();
  };

  const openMediaManager = () => {
    setIsOpen(true);
  };

  return (
    <div id="media-manager">
      {multiple ? (
        canAdd ? (
          <Stack direction="row" justifyContent="center" spacing={2} >
            <LoadingButton
              loading={isOpen && uploadImageMutation.isLoading}
              size="medium"
              fullWidth={false}
              endIcon={<HugeiconsIcon icon={Image02Icon} />}
              onClick={openMediaManager}
              disabled={mediaResults.images.length >= MAX_FILES}
            >
              Elegir
            </LoadingButton>
            <LoadingButton
              loading={!isOpen && uploadImageMutation.isLoading}
              variant="outlined"
              size="medium"
              fullWidth={false}
              startIcon={<HugeiconsIcon icon={ImageUploadIcon} />}
              onClick={open}
              disabled={mediaResults.images.length >= MAX_FILES}
            >
              Subir
            </LoadingButton>
            <input {...getInputProps()} />
          </Stack>
        ) : null
      ) : (
        <Stack gap={2}>
          {label ? (
            <Typography variant="h4">{label}</Typography>
          ) : null}
          <Stack
            gap={2}
            flex={1}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <ButtonBase onClick={openMediaManager}>
              <Avatar
                src={value}
                sx={{
                  width: 75,
                  height: 75,
                  backgroundColor: '#DDEAFD',
                }}
                variant="rounded"
              >
                {!value ? <HugeiconsIcon icon={Tree01Icon} color={palette.primary.main} /> : undefined}
              </Avatar>
            </ButtonBase>
            <Stack direction="row" alignItems="center">
              <IconButton
                onClick={openMediaManager}
                color="primary"
                disabled={mediaResults.images.length >= MAX_FILES}
              >
                <HugeiconsIcon icon={Upload01Icon} />
              </IconButton>
              {onDelete ? (
                <IconButton onClick={onDelete} color="error">
                  <HugeiconsIcon icon={Delete02Icon} />
                </IconButton>
              ) : null}
            </Stack>
          </Stack>
          {helperText ? (
            <FormHelperText error={!!helperText}>{helperText}</FormHelperText>
          ) : null}
        </Stack>
      )}

      <Dialog
        open={isOpen}
        maxWidth="md"
        onClose={handleClose}
        PaperProps={{
          className: 'media-manager',
        }}
        {...getRootProps({ className: 'dropzone' })}
        scroll="paper"
        fullScreen={isMobile}
      >
        <Stack
          className="media-manager__header"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <IconButton onClick={handleClose}>
              <HugeiconsIcon icon={ArrowLeft01Icon} />
            </IconButton>
            <DialogTitle>Media</DialogTitle>
          </Stack>

          <Stack direction="row" spacing={2}>
            {canUpload ? (
              <Button
                onClick={open}
                disabled={imagesQuery.isLoading || mediaResults.images.length >= MAX_FILES}
                startIcon={<HugeiconsIcon icon={Upload01Icon} />}
                variant="text"
                autoFocus
              >
                Subir Archivo
              </Button>
            ) : null}
            {canAdd && selectedImages.length ? (
              <>
                <Tooltip title="Usar seleccionados" placement="top" arrow>
                  <Button
                    onClick={templateId ? handleMenuClick : () => handleChange('original')}
                    disabled={imagesQuery.isLoading}
                    size="medium"
                    color="success"
                    autoFocus
                  >
                    Usar
                  </Button>
                </Tooltip>
                {templateId && (
                  <Menu
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleChange('original')}>Original</MenuItem>
                    <MenuItem onClick={() => handleChange('800x500')}>Miniatura (800x500)</MenuItem>
                    <MenuItem onClick={() => handleChange('150x150')}>Miniatura (150x150)</MenuItem>
                  </Menu>
                )}
              </>
            ) : null}
          </Stack>
          <input {...getInputProps()} />
        </Stack>

        {imagesQuery.isLoading || uploadImageMutation.isLoading || deleteImageMutation.isLoading ? <LinearProgress /> : null}

        <DialogContent ref={scrollContainer} className="media-manager__body" sx={{ maxHeight: isMobile ? '100%' : 600 }}>
          {mediaResults.images.length >= MAX_FILES ? (
            <Alert severity="warning" sx={{ marginBottom: 2 }}>
              Haz alcanzado el límite de archivos permitidos
            </Alert>
          ) : null}
          <Grid container rowSpacing={3} columnSpacing={2}>
            {mediaResults.images.map((image) => (
              <Grid
                key={image}
                size={{ xs: 6, sm: 3, md: 3 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Thumbnail
                  image={image}
                  isSelected={selectedImages.includes(image)}
                  onClick={handleSelection}
                />
              </Grid>
            ))}
          </Grid>
          <Stack ref={newInfiniteRef} py={7} alignItems="center">
            { mediaResults?.nextPageToken !== null ? (
              <CircularProgress size={24}/>
            ) : null}
          </Stack>
        </DialogContent>

        {selectedImages.length ? (
          <div className="media-manager__actions">
            <Stack spacing={1} direction="row" alignItems="center">
              <Tooltip title="Limpiar selección" placement="top" arrow>
                <IconButton onClick={handleClearSelection}>
                  <HugeiconsIcon icon={Cancel01Icon} />
                </IconButton>
              </Tooltip>
              <Typography variant="body1">
                {selectedImages.length}{' '}
                {pluralize('Seleccionado', selectedImages.length)}
              </Typography>
            </Stack>
            <Stack spacing={1} direction="row" alignItems="center">
              {canDelete ? (
                <Tooltip title="Eliminar" placement="top" arrow>
                  <IconButton
                    onClick={handleDelete}
                    disabled={imagesQuery.isLoading}
                    size="medium"
                    color="error"
                    autoFocus
                  >
                    <HugeiconsIcon icon={Delete02Icon} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

export default MediaManager;
